import Bluebird from 'bluebird';
import moment from 'moment';

import { generateSessionId, logErrorWithContext, trackSessionEnd, trackSessionStart } from './sentry/sentrySetup';
import { padZeroes } from './file';
import { getServerTime } from './getServerTime';
import { ReportFileMetadataUsageEnum, ReportFileTypeEnum, uploadReportAttachment } from './reportAttachmentSignedUrl';

type SectionMeta = {
  section: number;
  uid: string;
};

function getFileTypeEnum(typeFile?: string): ReportFileTypeEnum {
  if (typeFile === 'video') return ReportFileTypeEnum.VIDEO;
  if (typeFile === 'document') return ReportFileTypeEnum.DOCUMENT;
  return ReportFileTypeEnum.IMAGE; // 'photo' or default
}

const uploadReportDocuments = (
  documentURI: File | string,
  _organizationKey: string,
  siteKey: string,
  reportKey: string,
  _sectionData: SectionMeta | false,
  cancelHandler: () => any,
  _isTutorial?: boolean,
  _onSuccess?: (ref: string) => any,
  questionIndex?,
  fileIndex?,
  typeFile?: string
): Promise<string> => {
  const uploadSessionId = generateSessionId();
  const startTime = Date.now();

  trackSessionStart('document_upload', uploadSessionId, {
    report_key: reportKey,
    type_file: typeFile
  });

  return new Bluebird((resolve: (T: any) => void, reject: (E: Error) => any, onCancel: (A?: any) => any) => {
    // Already-uploaded path — passthrough
    if (typeof documentURI === 'string') {
      trackSessionEnd('document_upload', uploadSessionId, 'success', { duration_ms: 0, already_uploaded: true });
      return resolve(documentURI);
    }

    if (onCancel) {
      onCancel(cancelHandler);
    }

    const timestamp = moment(getServerTime()).format('YYYYMMDD-HHmmss');
    const fileExt = (documentURI as File).name.split('.').pop();
    const blob = (documentURI as File).slice(0, (documentURI as File).size, (documentURI as File).type);

    const questionNumberSerial = padZeroes((questionIndex ?? 0) + 1, 3);
    const newFileName = `Q${questionNumberSerial}${
      fileIndex !== undefined ? `-${fileIndex + 1}-` : ''
    }${timestamp}.${fileExt}`;
    const newNameFile = new File([blob], newFileName, { type: (documentURI as File).type });

    uploadReportAttachment({
      body: {
        fileName: newNameFile.name,
        fileType: getFileTypeEnum(typeFile),
        mimeType: newNameFile.type || 'application/octet-stream',
        reportID: reportKey,
        siteID: siteKey,
        usageType: ReportFileMetadataUsageEnum.QUESTION_ATTACHMENTS,
        questionIndex: questionIndex ?? -1,
        childQuestionIndex: fileIndex !== undefined ? String(fileIndex) : ''
      },
      file: newNameFile
    })
      .then(filePath => {
        const duration = Date.now() - startTime;
        trackSessionEnd('document_upload', uploadSessionId, 'success', { duration_ms: duration });
        resolve(filePath);
      })
      .catch((error: any) => {
        const duration = Date.now() - startTime;
        trackSessionEnd('document_upload', uploadSessionId, 'failure', { duration_ms: duration });
        logErrorWithContext(error, 'upload_document_error', {
          session_id: uploadSessionId,
          duration_ms: duration,
          report_key: reportKey
        });
        reject(error);
      });
  });
};

export default uploadReportDocuments;
